import './Message.scss';
import React, {useState} from 'react';
import {Avatar, Box, IconButton, Paper, Tooltip, Typography} from '@mui/material';
import {Check, ContentCopyOutlined, ThumbDownOutlined, ThumbUpOutlined} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {SyntaxHighlighterProps} from 'react-syntax-highlighter';
import {Prism} from 'react-syntax-highlighter';
import {oneDark, oneLight} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {useTheme} from '@mui/material/styles';
import type {ChatMessage} from '../types/chatTypes';
import {ChartRenderer} from './ChartRenderer';
import {FeedbackModal} from './FeedbackModal';
import {useChatContext} from '../context/ChatContext';
import {useStores} from '../../../stores';
import MithraIcon from '../../../../public/chat/MithraSmallLogo.svg?react';
import UserIcon from '../../../../public/chat/ChatUser.svg?react';
import DataRetrievalIcon from '../../../../public/chat/data_retreival.svg?react';
import CreatingReportIcon from '../../../../public/chat/creating_report.svg?react';
import ExportIcon from '../../../../public/chat/export.svg?react';
import InternetSearchIcon from '../../../../public/chat/internet_search.svg?react';
import DotsIcon from '../../../../public/chat/dots.svg?react';
import TickIcon from '../../../../public/chat/Tick.svg?react';

const SyntaxHighlighter = Prism as never as typeof React.Component<SyntaxHighlighterProps>;

type Props = {
    message: ChatMessage;
}

// Function name to text and icon mapping
const functionConfig: Record<string, {
    text: { function_call: string; function_response: string };
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
}> = {
    execute_sql: {
        text: {
            function_call: 'Data Retrieval In Progress.',
            function_response: 'Data Retrieval Complete!'
        },
        icon: DataRetrievalIcon
    },
    create_report: {
        text: {
            function_call: 'Report Generation In Progress.',
            function_response: 'Report Generation Complete!'
        },
        icon: CreatingReportIcon
    },
    export_data: {
        text: {
            function_call: 'Export In Progress.',
            function_response: 'Export Ready!'
        },
        icon: ExportIcon
    },
    internet_search: {
        text: {
            function_call: 'Internet Search In Progress.',
            function_response: 'Internet Search Complete!'
        },
        icon: InternetSearchIcon
    }
};

// Default fallback
const defaultConfig = {
    text: {
        function_call: 'Processing In Progress.',
        function_response: 'Processing Complete!'
    },
    icon: DataRetrievalIcon
};

export const Message: React.FC<Props> = React.memo(({message}) => {
    const theme = useTheme();
    const {currentSession} = useChatContext();
    const {chatApiStore} = useStores();
    const [copied, setCopied] = useState(false);

    const [feedbackUpLoading, setFeedbackUpLoading] = useState(false);
    const [feedbackDownLoading, setFeedbackDownLoading] = useState(false);
    const [feedbackGiven, setFeedbackGiven] = useState<'up' | 'down' | null>(null);

    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const isUser = message.type === 'user';
    const isFunction = message.event_type === 'function_call' || message.event_type === 'function_response';

    // Extract function name from content (assuming it's the content itself or parse if needed)
    const functionName = isFunction ? message.content : '';

    // Get the config for this function
    const config = functionName && functionConfig[functionName]
        ? functionConfig[functionName]
        : defaultConfig;

    // Handle transitioning state - show previous event type during animation
    const currentEventType = message.isTransitioning && message.previousEventType
        ? message.previousEventType
        : message.event_type;

    // Get the appropriate text based on event type
    const functionText = currentEventType === 'function_call'
        ? config.text.function_call
        : config.text.function_response;

    // Get the status indicator based on event type
    const StatusIndicator = currentEventType === 'function_call' ? DotsIcon : TickIcon;

    // Handle copy to clipboard
    const handleCopy = async () => {
        setFeedbackGiven(null) // testin
        try {
            await navigator.clipboard.writeText(message.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    // Handle thumbs up
    const handleThumbsUp = async () => {
        if (!currentSession) return;
        setFeedbackUpLoading(true);
        try {
            await chatApiStore.saveFeedback(currentSession.id, message.id, true, null, null);
            setFeedbackGiven('up');
        } finally {
            setFeedbackUpLoading(false);
        }
    };

    // Handle thumbs down - open modal
    const handleThumbsDown = () => {
        if (!currentSession) return;
        setFeedbackModalOpen(true);
    };

    // Handle feedback modal submit
    const handleFeedbackSubmit = async (category: string | null, description: string | null) => {
        if (!currentSession) return;
        setFeedbackDownLoading(true);
        try {
            await chatApiStore.saveFeedback(currentSession.id, message.id, false, category, description);
            setFeedbackGiven('down');
            setFeedbackModalOpen(false);
        } finally {
            setFeedbackDownLoading(false);
        }
    };

    // Preprocess the message content to handle indentation issues
    const processedContent = React.useMemo(() => {
        return message.content
        if (isUser) return message.content;

        const lines = message.content.split('\n');
        const processedLines: string[] = [];
        let inFencedCodeBlock = false;
        let inChartBlock = false;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Track fenced code blocks (```)
            if (/^\s*```/.test(line)) {
                if (/^\s*```chart\s*$/.test(line)) {
                    // This is a chart block - normalize it
                    inChartBlock = true;
                    processedLines.push('```chart');
                } else if (inChartBlock && /^\s*```\s*$/.test(line)) {
                    // End of chart block
                    inChartBlock = false;
                    processedLines.push('```');
                } else {
                    // Regular code block
                    inFencedCodeBlock = !inFencedCodeBlock;
                    processedLines.push(line);
                }
            }
            // Inside chart blocks, remove indentation
            else if (inChartBlock) {
                processedLines.push(line.replace(/^\s{4,}/, ''));
            }
            // Inside regular fenced code blocks, keep as-is
            else if (inFencedCodeBlock) {
                processedLines.push(line);
            }
                // For regular content, remove excessive indentation that would create code blocks
            // In Markdown, 4+ spaces create code blocks, so we normalize this
            else {
                // Remove leading whitespace that would cause Markdown to treat as code blocks
                const trimmedLine = line.replace(/^\s{4,}/, '');
                processedLines.push(trimmedLine);
            }
        }

        return processedLines.join('\n');
    }, [message.content, isUser]);

    // Custom components for markdown rendering (memoized to prevent unnecessary re-renders)
    const components = React.useMemo(() => ({
        code({node, inline, className, children, ...props}: any) {
            const match = /language-(\w+)/.exec(className || '');
            const content = String(children).replace(/\n$/, '');
            console.log('Code block detected:', {inline, className, content: content.substring(0, 50)});

            const isInline = !className && !content.includes('\n');

            // Handle chart rendering for non-inline code blocks
            if (!isInline) {
                // Check for fenced code blocks with language specification
                if (match) {
                    const language = match[1];

                    // Check if this is a chart block
                    if (language === 'chart' || language === 'echarts') {
                        return <ChartRenderer config={content}/>;
                    }

                    // Regular syntax highlighting for other code blocks
                    return (
                        <SyntaxHighlighter
                            style={theme.palette.mode === 'dark' ? oneDark : oneLight}
                            language={language}
                            PreTag="div"
                            {...props}
                        >
                            {content}
                        </SyntaxHighlighter>
                    );
                }

                // Check for chart content in regular code blocks (no language specified)
                // This handles indented code blocks that might contain chart JSON
                try {
                    const trimmedContent = content.trim();
                    if (trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) {
                        const parsed = JSON.parse(trimmedContent);
                        // Check if this looks like an ECharts configuration
                        if (parsed && (parsed.series || parsed.xAxis || parsed.yAxis || parsed.title)) {
                            return <ChartRenderer config={trimmedContent}/>;
                        }
                    }
                } catch (e) {
                    // Not valid JSON, continue with regular code rendering
                }

                // Regular code block without language specification
                return (
                    <SyntaxHighlighter
                        style={theme.palette.mode === 'dark' ? oneDark : oneLight}
                        language="javascript"
                        PreTag="div"
                        {...props}
                    >
                        {content}
                    </SyntaxHighlighter>
                );
            }

            console.log('Rendering as inline code');
            // Inline code
            return (
                <Box
                    component="code"
                    sx={{
                        display: 'inline', // Make sure it's inline to flow with text
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#0d0d0d' : '#f1f5f9',
                        color: (theme) => theme.palette.mode === 'dark' ? '#a78bfa' : '#7c3aed',
                        px: 0.3,
                        py: 0.2,
                        borderRadius: 0.5,
                        fontFamily: 'JetBrains Mono, Fira Code, SF Mono, Monaco, Consolas, Ubuntu Mono, monospace',
                        fontSize: '0.9em',
                        border: (theme) => theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.06)'
                            : '1px solid rgba(0,0,0,0.06)',
                        fontWeight: 500,
                        whiteSpace: 'nowrap', // Prevent wrapping within the code element
                    }}
                    {...props}
                >
                    {children}
                </Box>
            );
        },
        p: ({children}: any) => (
            <Typography
                variant="body1"
                component="span"
                sx={{mb: 1.5, wordWrap: 'break-word'}}
            >
                {children}
            </Typography>
        ),
        ul: ({children}: any) => (
            <Box component="ul" sx={{m: 1.5, pl: 2.5}}>
                {children}
            </Box>
        ),
        ol: ({children}: any) => (
            <Box component="ol" sx={{m: 1.5, pl: 2.5}}>
                {children}
            </Box>
        ),
        li: ({children}: any) => (
            <Typography component="li" variant="body1" sx={{mb: 0.75, wordWrap: 'break-word'}}>
                {children}
            </Typography>
        ),
        blockquote: ({children}: any) => (
            <Paper
                sx={{
                    borderLeft: '4px solid #00d4aa',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#141414' : '#f8fafc',
                    borderRadius: '0 12px 12px 0',
                    p: 2,
                    m: 2,
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    border: (theme) => theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.04)'
                        : '1px solid rgba(0,0,0,0.04)',
                }}
            >
                {children}
            </Paper>
        ),
        h1: ({children}: any) => (
            <Typography variant="h4" sx={{mt: 2.5, mb: 1.5, fontWeight: 600}}>
                {children}
            </Typography>
        ),
        h2: ({children}: any) => (
            <Typography variant="h5" sx={{mt: 2.5, mb: 1.5, fontWeight: 600}}>
                {children}
            </Typography>
        ),
        h3: ({children}: any) => (
            <Typography variant="h6" sx={{mt: 2.5, mb: 1.5, fontWeight: 600}}>
                {children}
            </Typography>
        ),
        h4: ({children}: any) => (
            <Typography variant="subtitle1" sx={{mt: 2.5, mb: 1.5, fontWeight: 600}}>
                {children}
            </Typography>
        ),
        h5: ({children}: any) => (
            <Typography variant="subtitle2" sx={{mt: 2.5, mb: 1.5, fontWeight: 600}}>
                {children}
            </Typography>
        ),
        h6: ({children}: any) => (
            <Typography variant="body1" sx={{mt: 2.5, mb: 1.5, fontWeight: 600}}>
                {children}
            </Typography>
        ),
    }), [theme]);

    return (
        <Box
            className="chat-message message-fade-in"
            sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                maxWidth: '100%',
                flexDirection: isUser ? 'row-reverse' : 'row',
            }}
        >
            <Box sx={{
                position: 'relative',
                flexShrink: 0,
                width: isFunction ? 44 : 'auto',
                height: isFunction ? 44 : 'auto'
            }}>
                <Avatar
                    className={`chat-avatar ${
                        isUser ? 'user-avatar' :
                            isFunction ?
                                (message.event_type === 'function_call' ? 'function-call-avatar' : 'function-response-avatar') :
                                'bot-avatar'
                    }`}
                    sx={{
                        ...(isFunction && {
                            width: 40,
                            height: 40,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            border: '1px solid !important',
                            borderColor: 'background.paper',
                        }),
                    }}
                >
                    {isUser ? (
                        <UserIcon
                            style={{
                                width: 24,
                                height: 24,
                            }}
                        />
                    ) : isFunction ? (
                        React.createElement(config.icon, {
                            style: {
                                width: 40,
                                height: 40,
                            }
                        })
                    ) : (
                        <MithraIcon
                            style={{
                                width: 45,
                                height: 45,
                            }}
                        />
                    )}
                </Avatar>
                {isFunction && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <StatusIndicator
                            style={{
                                width: 16,
                                height: 16,
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: {
                        xs: 'calc(80vw - 60px)', // 80% on mobile for better readability
                        sm: 'calc(70vw - 60px)', // 70% on tablet
                        md: 'calc(50vw - 60px)', // 50% on desktop and larger
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                }}
            >
                <Box
                    className="message-container"
                    sx={{
                        position: 'relative',
                    }}
                >
                    <Paper
                        className={`message-paper ${isUser ? 'user-message' : 'bot-message'} ${message.isTransitioning ? 'transitioning' : ''}`}
                        sx={{
                            p: isUser ? 2.5 : 3,
                            opacity: message.isTransitioning ? 0.3 : 1,
                            transition: message.isTransitioning
                                ? 'opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                                : 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                    >
                        {isUser ? (
                            <Typography
                                variant="body1"
                                className="user-message-text"
                            >
                                {message.content}
                            </Typography>
                        ) : isFunction ? (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 500,
                                }}
                            >
                                {functionText}
                            </Typography>
                        ) : (
                            <Box className="bot-message-content" sx={{
                                color: 'text.primary',
                            }}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={components}
                                >
                                    {message.isTransitioning && message.previousContent
                                        ? message.previousContent
                                        : processedContent}
                                </ReactMarkdown>
                            </Box>
                        )}
                    </Paper>

                    {/* Feedback buttons for agent messages */}
                    {!isUser && !isFunction && (
                        <Box
                            className="feedback-buttons"
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                mt: 0.5,
                            }}>
                            <Tooltip title={copied ? "Copied!" : "Copy"}>
                                <IconButton
                                    onClick={handleCopy}
                                    size="small"
                                >
                                    {copied
                                        ? <Check sx={{fontSize: 16}}/>
                                        : <ContentCopyOutlined sx={{fontSize: 16}}/>
                                    }
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Good response">
                                <IconButton
                                    onClick={handleThumbsUp}
                                    size="small"
                                    disabled={currentSession === null}
                                    color={feedbackGiven === 'up' ? 'success' : 'default'}
                                    loading={feedbackUpLoading}
                                >
                                    <ThumbUpOutlined sx={{fontSize: 16}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Bad response">
                                <IconButton
                                    onClick={handleThumbsDown}
                                    size="small"
                                    disabled={currentSession === null}
                                    color={feedbackGiven === 'down' ? 'warning' : 'default'}
                                    loading={feedbackDownLoading}
                                >
                                    <ThumbDownOutlined sx={{fontSize: 16}}/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>
            <FeedbackModal
                open={feedbackModalOpen}
                onClose={() => setFeedbackModalOpen(false)}
                onSubmit={handleFeedbackSubmit}
            />
        </Box>
    );
});
